import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

/**
 * A loading state that reassures instead of freezing: it rotates through warm
 * "still working" messages and shows a live elapsed counter, so a slow
 * operation (a long goal breakdown, a heavy chat turn) always looks actively
 * in progress rather than stuck. Drop it anywhere an async op is pending.
 */
const DEFAULT_MESSAGES = [
  'Working on it…',
  'Almost there…',
  'Just a moment more…',
  'Thanks for your patience…',
]

export function ReassuringLoader({
  title = 'Working on it…',
  messages = DEFAULT_MESSAGES,
  className = '',
}: {
  title?: string
  /** Rotated every ~5s. Falls back to a sensible default set. */
  messages?: string[]
  className?: string
}) {
  const lines = messages.length > 0 ? messages : DEFAULT_MESSAGES
  const [idx, setIdx] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const start = Date.now()
    const t = setInterval(() => {
      const ms = Date.now() - start
      setElapsed(Math.round(ms / 1000))
      setIdx(Math.floor(ms / 5000) % lines.length)
    }, 1000)
    return () => clearInterval(t)
  }, [lines.length])

  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 py-10 px-6 text-center ${className}`}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="w-7 h-7 animate-spin text-brand" />
      <div className="text-sm font-medium text-primary">{title}</div>
      <div className="text-xs text-secondary min-h-[1rem]">
        {lines[idx]}
        {elapsed > 6 ? ` · ${elapsed}s` : ''}
      </div>
      {/* Indeterminate bar — never fills to 100%, so it reads as "in progress". */}
      <div className="w-40 max-w-full bg-soft rounded-full h-1 overflow-hidden">
        <div className="bg-brand h-1 rounded-full w-full animate-pulse" />
      </div>
    </div>
  )
}
